import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';

import useAliasWishlist from 'hooks/useAliasWishlist';
import useModal from 'hooks/useModal';

import { sendEvent } from 'lib/gtag';

import { useUser } from 'providers/UserProvider';

import OFFER from 'constants/offer';

const WishlistSuggestionsModal = dynamic(
  () => import('./WishlistSuggestionsModal'),
  { ssr: false }
);

/**
 * Ofertas encerradas (FINISHED) ou em espera (WAITING) não fazem
 * sentido para alerta de preço — escondemos o CTA nesses casos.
 */
const isOfferAlertable = (offerStatusName) =>
  offerStatusName !== OFFER.STATUSES.FINISHED &&
  offerStatusName !== OFFER.STATUSES.WAITING;

const AlertButton = ({
  aliasId,
  className,
  gaEventCategory,
  keywordLabel = null,
  keywords,
  offerStatusName,
}) => {
  const { showModal } = useModal();
  const { runCallbackIfLoggedIn } = useUser();
  const [
    hasSuggestedKeywordAddedInWishlist,
    setHasSuggestedKeywordAddedInWishlist,
  ] = useState(false);
  const { addOfferWishlistAlias, removeOfferWishlistAlias, aliasWishlist } =
    useAliasWishlist(gaEventCategory, {
      gaEventLabel: 'offer_details',
    });

  const hasAliasTerm = useMemo(
    () => aliasWishlist?.items.some((item) => item.aliasId === aliasId),
    [aliasWishlist, aliasId]
  );

  const hasTerm = hasAliasTerm || hasSuggestedKeywordAddedInWishlist;

  if (!isOfferAlertable(offerStatusName)) {
    return null;
  }

  const onClick = () => {
    if (aliasId) {
      runCallbackIfLoggedIn(() => {
        if (hasAliasTerm) {
          removeOfferWishlistAlias({ aliasId });
          return;
        }

        addOfferWishlistAlias({ aliasId });
      });
      return;
    }

    sendEvent({
      action: 'show_wishlist_suggestions',
      category: gaEventCategory,
    });

    showModal(WishlistSuggestionsModal, {
      keywords,
      onAddKeyword: () => setHasSuggestedKeywordAddedInWishlist(true),
      onRemoveKeyword: () => setHasSuggestedKeywordAddedInWishlist(false),
    });
  };

  const labelPrefix = hasTerm ? 'Remover alerta' : 'Criar alerta';
  const ariaLabel = keywordLabel
    ? `${labelPrefix} ${keywordLabel}`
    : labelPrefix;
  const iconName = hasTerm ? 'notification-filled' : 'notification';

  /*
    Cores neutras (não-azuis) pro estado padrão — alinhado com o
    Pelando. Estado ativo mantém o fundo primary-300 pra feedback
    visual forte. O shopper Button aplica `text-primary-300` via
    `type="primary-ghost"`, por isso precisamos sobrescrever com `!`
    nos seletores `[&>span]`/`[&>svg]`.
  */
  const colorClasses = hasTerm
    ? '!border-primary-300 !bg-primary-300 [&>span]:!text-neutral-high-100 [&>svg]:!text-neutral-high-100'
    : '!border-neutral-high-400 dark:!border-neutral-high-500 [&>span]:!text-neutral-low-500 [&>svg]:!text-neutral-low-500 dark:[&>span]:!text-neutral-high-100 dark:[&>svg]:!text-neutral-high-100';

  /*
    Label em dois pedaços: prefixo herda o `bold` do `<Text>` interno
    do shopper Button; keyword ganha `font-regular` via span aninhado
    sobrescrevendo o peso. O `ml-1` (0.25rem) é margem CSS, não text
    node — evita o problema clássico de espaço branco colapsado em
    flex container (o wrapper `<Text>` do Button tem `flex items-
    center`).
  */
  const buttonContent = (
    <>
      {labelPrefix}
      {keywordLabel && (
        <span className="ml-1 font-regular">{keywordLabel}</span>
      )}
    </>
  );

  /*
    No mobile, o rótulo "Criar alerta {produto}" estoura a largura
    quando a keyword é longa e quebra em cima dos elementos vizinhos.
    Mostramos só o ícone redondo nessa faixa; a pílula com texto só
    aparece a partir de `lg`. Dois botões distintos (mesmo `onClick`)
    em vez de um só com `span` escondido, pra podermos ajustar tamanho
    e forma (pill vs round) em cada variante.
  */
  return (
    <>
      <Button
        aria-label={ariaLabel}
        className={twJoin(
          'size-10 rounded-full !border !p-0 min-size-10 lg:hidden',
          colorClasses,
          className
        )}
        icon={<Icon name={iconName} />}
        size="size4"
        type="primary-ghost"
        onClick={onClick}
      />
      <Button
        className={twJoin(
          'hidden rounded-full !border !px-3 !text-sm lg:inline-flex [&>span]:!text-sm',
          colorClasses,
          className
        )}
        iconLeft={<Icon name={iconName} />}
        size="size3"
        type="primary-ghost"
        onClick={onClick}
      >
        {buttonContent}
      </Button>
    </>
  );
};

AlertButton.propTypes = {
  aliasId: PropTypes.number,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  /**
   * Rótulo curto concatenado após "Criar alerta" — normalmente a
   * keyword mais relevante da oferta (ex.: "Galaxy A56"). Quando
   * ausente, só mostra "Criar alerta".
   */
  keywordLabel: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  offerStatusName: PropTypes.string.isRequired,
};

export default AlertButton;
